import React, { useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import PatientScalesPanel from './components/PatientScalesPanel';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import useAnalytics from '../../../hooks/useAnalytics';

function ScalesPreview() {
  const { patientId } = useParams();  
  const { patientDataModel } = useContext(PatientDataContext);
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  // Track page view when component mounts
  React.useEffect(() => {
    if (patientId) {
      trackEvent('scales_preview_viewed', {
        patientId,
        timestamp: new Date().toISOString()
      });
    }
  }, [patientId, trackEvent]);

  if (!patientDataModel) {
    return (
      <Layout activePage="dashboard">
        <div className="flex-grow pl-8 pr-8">
          <div className="w-full h-24 flex items-center justify-center">
            <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-gray-800 rounded-lg">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading scales...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Get patient name
  const patientName = `${patientDataModel.firstName} ${patientDataModel.lastName}`;

  return (
    <Layout activePage="dashboard">
      <div className="flex-grow px-4 md:px-8">
        {/* Container for centered desktop layout */}
        <div className="w-full max-w-4xl mx-auto">
          {/* Removed top navigation - moved to bottom-right */}

          {/* Page Header - Responsive alignment */}
          <div className="text-center pt-6 md:pt-6 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-2xl font-semibold text-gray-900 mb-1 md:mb-2">
              Rating Scales Preview
            </h1>
            <div className="text-gray-900 text-lg md:text-xl font-semibold">
              for {patientName}
            </div>
          </div>

          {/* Scales Panel - Centered with max width on desktop */}
          <div className="w-full">
            <PatientScalesPanel patientData={patientDataModel} displayMode="condensed" />
          </div>

          {/* Right-aligned navigation button below content */}
          <div className="flex justify-end mt-6 mb-6 md:mb-8">
            <button
              onClick={() => navigate(`/patient/${patientId}`)}
              className="inline-flex font-bold items-center gap-1 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white rounded-lg transition duration-100 shadow-lg"
              aria-label="Go to Patient Overview"
            >
              <span>Go to Patient Overview</span>
              <ChevronLeftIcon className="w-4 h-4 rotate-180" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ScalesPreview; 